/**
 * Approval boards (CAB) — Step 101.
 *
 * A board is a named, ordered list of stages: "first stage" then "second
 * stage", each asking the requester's manager, a group, or named people, and
 * each needing any one or all of them to say yes. Asking a board materialises
 * those stages into the chain a ticket already understands.
 *
 * Draft-first: a board and its stages are edited as a whole and saved with
 * one button. Stages reorder with the arrows — a `position` int, the same
 * pattern every orderable child row here uses.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ClipboardList, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { TokenSelect } from '../../components/ui/tokenselect';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';
import { cn } from '../../lib/utils';
import { RISK_ORDER, stageApplies } from '@itsm/shared';

interface Stage {
  id?: string;
  position?: number;
  name: string | null;
  mode: 'any' | 'all' | 'majority';
  source: 'manager' | 'group' | 'users' | 'ticket_group';
  groupId: string | null;
  userIds: string[];
  /** Step 195 — this stage only runs at this risk and above. */
  minRisk: string | null;
}
interface Board {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  catalogueItems: number;
  /* ── Step 195: when this board applies. Empty = any. ── */
  appliesToTypes: string[];
  appliesToKinds: string[];
  appliesToGroupIds: string[];
  minRisk: string | null;
  isMandatory: boolean;
  autoRaise: boolean;
  priority: number;
  stages: Stage[];
}
interface Payload {
  entries: Board[];
  groups: { id: string; name: string }[];
  people: { id: string; name: string | null; email: string }[];
}

const SOURCE_LABEL: Record<Stage['source'], string> = {
  manager: "the requester's line manager",
  group: 'anyone in a group',
  users: 'named people',
  /** Step 197 — resolved per change, not per board. */
  ticket_group: "the change's own team",
};

const emptyStage = (): Stage => ({
  name: null,
  mode: 'any',
  source: 'users',
  groupId: null,
  userIds: [],
  minRisk: null,
});

/** Step 195 — the risk scale, worst last. `''` is "any risk". */
const RISK_OPTIONS = [
  { value: '', label: 'Any risk' },
  { value: 'low', label: 'Low and above' },
  { value: 'medium', label: 'Medium and above' },
  { value: 'high', label: 'High and above' },
  { value: 'critical', label: 'Critical only' },
];
const KINDS = ['normal', 'standard', 'emergency'] as const;

export function ApprovalBoardsPage() {
  const [selectedId, setSelectedId] = useUrlState('board', null);
  const loaded = useQuery({
    queryKey: qk.approvalBoards,
    queryFn: () => apiFetch<Payload>('/api/admin/approval-boards'),
  });
  return (
    <div className="p-6">
      <div className="mb-1 flex items-center gap-3">
        <ClipboardList className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Approval boards</h1>
      </div>
      <p className="mb-5 max-w-2xl text-sm text-muted-foreground">
        A board is who signs off, in order — a change advisory board with a first and second stage, or a
        single group who approve new starters. Catalogue items can point at one, rules can send a ticket to
        one, and an agent can ask one from a ticket's Approvals tab. Editing a board never changes an approval
        already under way.
      </p>
      {loaded.isError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm font-medium">Boards could not be loaded.</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void loaded.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {loaded.isPending && <Skeleton className="h-48 w-full" />}
      {loaded.data && (
        <div className="grid gap-6 lg:grid-cols-[1fr,30rem]">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                {loaded.data.entries.length} {loaded.data.entries.length === 1 ? 'board' : 'boards'}
              </p>
              <div className="flex-1" />
              <Button size="sm" onClick={() => setSelectedId('new')}>
                <Plus /> New board
              </Button>
            </div>
            <div className="divide-y rounded-lg border bg-card">
              {loaded.data.entries.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setSelectedId(b.id)}
                  className={cn(
                    'flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    selectedId === b.id && 'bg-brand/5',
                    !b.isActive && 'opacity-60',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {b.name}
                      {!b.isActive && <Badge variant="secondary">Retired</Badge>}
                      <Badge variant="outline">
                        {b.stages.length} stage{b.stages.length === 1 ? '' : 's'}
                      </Badge>
                      {b.catalogueItems > 0 && (
                        <span className="text-xs font-normal text-muted-foreground">
                          used by {b.catalogueItems} catalogue item{b.catalogueItems === 1 ? '' : 's'}
                        </span>
                      )}
                    </p>
                    {b.description && (
                      <p className="truncate text-xs text-muted-foreground">{b.description}</p>
                    )}
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {b.stages.map((s, i) => `${i + 1}. ${s.name ?? SOURCE_LABEL[s.source]}`).join(' → ')}
                    </p>
                  </div>
                </button>
              ))}
              {loaded.data.entries.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No boards yet. A change advisory board is the usual first one.
                </p>
              )}
            </div>
          </div>
          <div>
            {selectedId ? (
              <BoardEditor
                key={selectedId}
                board={
                  selectedId === 'new' ? null : (loaded.data.entries.find((b) => b.id === selectedId) ?? null)
                }
                meta={loaded.data}
                onClose={() => setSelectedId(null)}
              />
            ) : (
              <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                Pick a board to edit it, or add one.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Which stages a change collects, at each risk — Step 202.
 *
 * Stage floors are CUMULATIVE and always have been: a floor means "this stage
 * and every lower one", so a low-risk change collects only the unconditional
 * stages and a critical one collects the lot. That is the behaviour people
 * want and could not see, which made it feel like it was not there.
 *
 * Reads the same `stageApplies` the server materialises with, so this cannot
 * drift into describing a rule the engine does not follow.
 */
function RiskLadder({ stages }: { stages: Stage[] }) {
  return (
    <div className="mb-3 space-y-1 rounded-md border border-dashed bg-muted/20 p-2">
      <p className="text-[11px] font-medium text-muted-foreground">Who gets asked, by risk</p>
      {RISK_ORDER.map((risk) => {
        const hit = stages
          .map((s, i) => ({ s, n: i + 1 }))
          .filter(({ s }) => stageApplies({ minRisk: s.minRisk }, risk));
        return (
          <p key={risk} className="flex gap-2 text-[11px]">
            <span className="w-14 shrink-0 font-medium capitalize">{risk}</span>
            <span className="text-muted-foreground">
              {hit.length
                ? `stage ${hit.map((h) => h.n).join(', ')}${hit.length > 1 ? ` — ${hit.length} approvals` : ''}`
                : 'nobody — this board would not apply'}
            </span>
          </p>
        );
      })}
      <p className="text-[11px] text-muted-foreground">
        A change with no risk recorded counts as critical, so it collects every stage.
      </p>
    </div>
  );
}

function BoardEditor({ board, meta, onClose }: { board: Board | null; meta: Payload; onClose: () => void }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const stored = {
    name: board?.name ?? '',
    description: board?.description ?? '',
    isActive: board?.isActive ?? true,
    appliesToKinds: board?.appliesToKinds ?? [],
    appliesToGroupIds: board?.appliesToGroupIds ?? [],
    minRisk: board?.minRisk ?? '',
    isMandatory: board?.isMandatory ?? true,
    autoRaise: board?.autoRaise ?? true,
    stages: board?.stages.map((s) => ({ ...s, userIds: [...s.userIds] })) ?? [emptyStage()],
  };
  const [draft, setDraft] = useState(stored);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  const clearDirty = useDirty(dirty);

  const stageProblem = (s: Stage) =>
    s.source === 'group' && !s.groupId
      ? 'names no group'
      : s.source === 'users' && s.userIds.length === 0
        ? 'names nobody'
        : null;
  // `ticket_group` names nothing on purpose — it is resolved per change.
  const invalid =
    !draft.name.trim() || draft.stages.length === 0 || draft.stages.some((s) => stageProblem(s));

  const setStage = (i: number, next: Partial<Stage>) =>
    setDraft({ ...draft, stages: draft.stages.map((s, j) => (i === j ? { ...s, ...next } : s)) });
  const move = (i: number, by: number) => {
    const next = [...draft.stages];
    const target = i + by;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target]!, next[i]!];
    setDraft({ ...draft, stages: next });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        isActive: draft.isActive,
        // Step 195 — a board for CHANGES. Kept explicit rather than inferred
        // so a later board for another type says so in the payload.
        appliesToTypes: ['change'],
        appliesToKinds: draft.appliesToKinds,
        appliesToGroupIds: draft.appliesToGroupIds,
        minRisk: draft.minRisk || null,
        isMandatory: draft.isMandatory,
        autoRaise: draft.autoRaise,
        stages: draft.stages.map((s) => ({
          name: s.name?.trim() || null,
          mode: s.mode,
          source: s.source,
          groupId: s.source === 'group' ? s.groupId : null,
          userIds: s.source === 'users' ? s.userIds : [],
          minRisk: s.minRisk || null,
        })),
      };
      return board
        ? apiSend(`/api/admin/approval-boards/${board.id}`, 'PUT', body)
        : apiSend<{ id: string }>('/api/admin/approval-boards', 'POST', body);
    },
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That could not be saved.');
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.approvalBoards });
      void qc.invalidateQueries({ queryKey: qk.approvalBoardPicker });
      // Step 171 — clear the guard before navigating. Closing this editor is a
      // URL change, so otherwise the blocker asks about the save that just
      // succeeded, and the panel stays open behind the dialog.
      clearDirty();
      toast.success(board ? 'Saved. Approvals already under way are unchanged.' : 'Board added.');
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => apiSend(`/api/admin/approval-boards/${board!.id}`, 'DELETE'),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That did not work.');
      void qc.invalidateQueries({ queryKey: qk.approvalBoards });
      clearDirty();
      void qc.invalidateQueries({ queryKey: qk.approvalBoardPicker });
      onClose();
    },
  });

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b p-4">
        <p className="font-semibold">{board ? board.name : 'New approval board'}</p>
      </div>
      <div className="space-y-3 p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. Radio CAB"
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Description</label>
          <Textarea
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            placeholder="What this board signs off — shown where it is offered"
            className="min-h-[56px]"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input"
            checked={draft.isActive}
            onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
          />
          <span>
            Available
            <span className="ml-1 text-xs text-muted-foreground">
              — a retired board stops being offered; approvals under way finish.
            </span>
          </span>
        </label>

        {/* ── When this board applies — Step 195 ──────────────────────────
            Until this existed, a board was a named list of stages and nothing
            could route a change to one: whoever raised the approval picked
            from every board in the org, by hand. */}
        <div className="space-y-3 border-t pt-3">
          <p className="text-xs font-medium text-muted-foreground">
            When this applies — leave a box empty to mean "any"
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Risk</label>
              <Select
                value={draft.minRisk}
                onChange={(e) => setDraft({ ...draft, minRisk: e.target.value })}
                aria-label="Minimum risk"
              >
                {RISK_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                A change with no risk recorded counts as the worst — unassessed is not the same as safe.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Change kinds</label>
              <TokenSelect
                value={draft.appliesToKinds}
                onChange={(v) => setDraft({ ...draft, appliesToKinds: v })}
                ariaLabel="Change kinds"
                options={KINDS.map((k) => ({ value: k, label: k }))}
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Owning groups — whose changes this board handles
            </label>
            <TokenSelect
              value={draft.appliesToGroupIds}
              onChange={(v) => setDraft({ ...draft, appliesToGroupIds: v })}
              ariaLabel="Owning groups"
              options={meta.groups.map((g) => ({ value: g.id, label: g.name }))}
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-input"
              checked={draft.autoRaise}
              onChange={(e) => setDraft({ ...draft, autoRaise: e.target.checked })}
            />
            <span>
              Ask automatically
              <span className="ml-1 text-xs text-muted-foreground">
                — raise this board the moment a matching change reaches Awaiting approval, without anybody
                having to remember.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-input"
              checked={draft.isMandatory}
              onChange={(e) => setDraft({ ...draft, isMandatory: e.target.checked })}
            />
            <span>
              Required
              <span className="ml-1 text-xs text-muted-foreground">
                — a matching change cannot leave Awaiting approval without a decision. Turn this off for a
                board that advises rather than gates.
              </span>
            </span>
          </label>
        </div>

        <div className="border-t pt-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            Stages — asked in order, one after the previous says yes
          </p>
          {/* Step 202 — show what the risk floors actually DO.
              Kai: *"you require up to step 1 for a low, but up to say step 4
              for critical"* — which is exactly how they already behaved since
              Step 195. Nothing on this page said so, so the only way to find
              out was to raise a change and watch. Four rows of arithmetic
              answer it before anybody saves. */}
          <RiskLadder stages={draft.stages} />
          <div className="space-y-3">
            {draft.stages.map((s, i) => {
              const problem = stageProblem(s);
              return (
                <div key={i} className={cn('rounded-md border p-3', problem && 'border-destructive/40')}>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[11px] font-medium">
                      {i + 1}
                    </span>
                    <Input
                      value={s.name ?? ''}
                      onChange={(e) => setStage(i, { name: e.target.value })}
                      placeholder={`Stage ${i + 1} name — optional, e.g. "first stage"`}
                      className="h-8 flex-1 text-xs"
                      aria-label={`Stage ${i + 1} name`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Move stage ${i + 1} up`}
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Move stage ${i + 1} down`}
                      disabled={i === draft.stages.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove stage ${i + 1}`}
                      disabled={draft.stages.length === 1}
                      onClick={() => setDraft({ ...draft, stages: draft.stages.filter((_, j) => j !== i) })}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                  {/* Labelled, not just aria-labelled — Step 203. Three bare
                      dropdowns in a stack is a guessing game: Kai, hunting for
                      the risk floor, asked *"Where's this chosen per cab
                      though?"* about a control that was sitting right here. */}
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[11px] text-muted-foreground">Who approves</label>
                      <Select
                        value={s.source}
                        onChange={(e) =>
                          setStage(i, {
                            source: e.target.value as Stage['source'],
                            groupId: null,
                            userIds: [],
                          })
                        }
                        className="h-8 w-full text-xs"
                        aria-label={`Stage ${i + 1} approvers`}
                      >
                        {/* Step 197 — resolved from the CHANGE, so one board
                          serves every team. A fixed group cannot say "whoever
                          owns this one". */}
                        <option value="ticket_group">The change's own team</option>
                        <option value="manager">The requester's line manager</option>
                        <option value="group">Anyone in a named group</option>
                        <option value="users">Named people</option>
                      </Select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] text-muted-foreground">How many of them</label>
                      <Select
                        value={s.mode}
                        onChange={(e) => setStage(i, { mode: e.target.value as Stage['mode'] })}
                        className="h-8 w-full text-xs"
                        aria-label={`Stage ${i + 1} rule`}
                      >
                        <option value="any">Any one of them approves</option>
                        <option value="majority">A majority of them approves</option>
                        <option value="all">All of them must approve</option>
                      </Select>
                    </div>
                  </div>
                  {/* Step 195 — Kai: "second step etc is active IF the change
                      risk is above X". A stage below the floor is left OUT of
                      the chain, never created-and-auto-approved: an approval
                      nobody was asked for must not appear in the audit trail
                      as though somebody gave it. */}
                  <div className="mt-2">
                    <label className="mb-1 block text-[11px] text-muted-foreground">
                      When this stage applies
                      <span className="ml-1">— the risk floor, per stage</span>
                    </label>
                    <Select
                      value={s.minRisk ?? ''}
                      onChange={(e) => setStage(i, { minRisk: e.target.value || null })}
                      className="h-8 w-full text-xs"
                      aria-label={`Stage ${i + 1} risk floor`}
                    >
                      <option value="">Always — every change reaches this stage</option>
                      <option value="medium">Medium risk and above</option>
                      <option value="high">High risk and above</option>
                      <option value="critical">Critical risk only</option>
                    </Select>
                  </div>
                  {s.source === 'group' && (
                    <Select
                      value={s.groupId ?? ''}
                      onChange={(e) => setStage(i, { groupId: e.target.value || null })}
                      className="mt-2 h-8 text-xs"
                      aria-label={`Stage ${i + 1} group`}
                    >
                      <option value="">Choose a group…</option>
                      {meta.groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </Select>
                  )}
                  {s.source === 'users' && (
                    <div className="mt-2">
                      {/* Requesters included, not only agents — the person signing
                          off a change is often a manager with no agent licence. */}
                      <TokenSelect
                        value={s.userIds}
                        options={meta.people.map((p) => ({ value: p.id, label: p.name ?? p.email }))}
                        onChange={(v) => setStage(i, { userIds: v })}
                        placeholder="Search people to ask…"
                        ariaLabel={`Stage ${i + 1} approvers`}
                      />
                    </div>
                  )}
                  {s.source === 'manager' && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Read from the directory at the moment approval is asked. A requester with no manager on
                      record cannot pass this stage, and is told why.
                    </p>
                  )}
                  {problem && <p className="mt-2 text-xs text-destructive">This stage {problem}.</p>}
                </div>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => setDraft({ ...draft, stages: [...draft.stages, emptyStage()] })}
          >
            <Plus /> Add a stage
          </Button>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex items-center justify-end gap-2 border-t pt-3">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => setDraft(stored)}
          >
            Discard
          </Button>
          <Button size="sm" disabled={!dirty || invalid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : board ? 'Save' : 'Add board'}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2 border-t p-4">
        {board && (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive"
            onClick={async () => {
              if (
                await confirm({
                  title: `Delete “${board.name}”?`,
                  body: 'Refused while a catalogue item points at it. Approvals already under way keep their approvers. Retiring it instead keeps the record.',
                  destructive: true,
                  confirmLabel: 'Delete',
                })
              )
                remove.mutate();
            }}
          >
            <Trash2 /> Delete
          </Button>
        )}
        <div className="flex-1" />
        <button onClick={onClose} className="text-xs text-muted-foreground hover:underline">
          Close
        </button>
      </div>
    </div>
  );
}
